import { createSlice } from "@reduxjs/toolkit";

import {
  createBulkTaskThunk,
  createTaskThunk,
  deleteTaskThunk,
  fetchQuickTaskThunk,
  fetchTasksThunk,
  updateTaskStatusThunk,
  updateTaskThunk,
} from "../thunks/tasksThunks";

import type {
  QuickTask,
  Task,
  TaskFilters,
  TaskStatus,
  TaskType,
} from "../../utils/types/tasks";
import type { PayloadAction } from "@reduxjs/toolkit";

interface TasksState {
  tasks: Task[];
  loading: boolean;
  error: string | null;
  filters: TaskFilters;
  total: number;
  quickTask: QuickTask[];
  quickTaskLoading: boolean;
  quickTaskError: string | null;
}

const initialState: TasksState = {
  tasks: [],
  loading: false,
  error: null,
  filters: {
    search: "",
    myTasksOnly: false,
    vesselStatus: null,
    taskType: null,
  },
  total: 0,
  quickTask: [],
  quickTaskLoading: false,
  quickTaskError: null,
};

const tasksSlice = createSlice({
  name: "tasks",
  initialState,
  reducers: {
    setSearchFilter(state, action: PayloadAction<string>) {
      state.filters.search = action.payload;
    },
    setMyTasksOnly(state, action: PayloadAction<boolean>) {
      state.filters.myTasksOnly = action.payload;
    },
    setVesselStatusFilter(state, action: PayloadAction<string | null>) {
      state.filters.vesselStatus = action.payload;
    },
    setTaskTypeFilter(state, action: PayloadAction<string | null>) {
      state.filters.taskType = action.payload;
    },
    clearFilters(state) {
      state.filters = {
        search: "",
        myTasksOnly: false,
        vesselStatus: null,
        taskType: null,
      };
    },
    // Optimistic update for drag and drop
    moveTask(
      state,
      action: PayloadAction<{ taskId: string; newStatus: TaskStatus }>
    ) {
      const { taskId, newStatus } = action.payload;
      const task = state.tasks.find((t) => t.id === taskId);
      if (task) {
        task.status = newStatus;
      }
    },
    reorderTasks(
      state,
      action: PayloadAction<{
        status: TaskStatus;
        sourceIndex: number;
        destinationIndex: number;
      }>
    ) {
      const { status, sourceIndex, destinationIndex } = action.payload;
      const tasksInColumn = state.tasks.filter((t) => t.status === status);
      const [removed] = tasksInColumn.splice(sourceIndex, 1);
      tasksInColumn.splice(destinationIndex, 0, removed);

      // Update main tasks array with new order
      state.tasks = state.tasks.filter((t) => t.status !== status);
      state.tasks.push(...tasksInColumn);
    },
  },
  extraReducers: (builder) => {
    // Fetch tasks
    builder
      .addCase(fetchTasksThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchTasksThunk.fulfilled,
        (state, action: PayloadAction<{ data: Task[]; total: number }>) => {
          state.loading = false;
          state.tasks = action.payload.data;
          state.total = action.payload.total;
        }
      )
      .addCase(fetchTasksThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create task
    builder
      .addCase(createTaskThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createTaskThunk.fulfilled,
        (state, action: PayloadAction<Task>) => {
          state.loading = false;
          state.tasks.push(action.payload);
          state.total += 1;
        }
      )
      .addCase(createTaskThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update task
    builder
      .addCase(updateTaskThunk.pending, (state) => {
        state.error = null;
      })
      .addCase(
        updateTaskThunk.fulfilled,
        (state, action: PayloadAction<Task>) => {
          const index = state.tasks.findIndex(
            (t) => t.id === action.payload.id
          );
          if (index !== -1) {
            state.tasks[index] = action.payload;
          }
        }
      )
      .addCase(updateTaskThunk.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Update task status
    builder
      .addCase(
        updateTaskStatusThunk.fulfilled,
        (state, action: PayloadAction<Task>) => {
          const index = state.tasks.findIndex(
            (t) => t.id === action.payload.id
          );
          if (index !== -1) {
            state.tasks[index] = action.payload;
          }
        }
      )
      .addCase(updateTaskStatusThunk.rejected, (state, action) => {
        state.error = action.payload as string;
        // Revert optimistic update would happen here if needed
      });

    // Delete task
    builder
      .addCase(
        deleteTaskThunk.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.tasks = state.tasks.filter((t) => t.id !== action.payload);
          state.total -= 1;
        }
      )
      .addCase(deleteTaskThunk.rejected, (state, action) => {
        state.error = action.payload as string;
      });
    // Fetch quick task
    builder
      .addCase(fetchQuickTaskThunk.pending, (state) => {
        state.quickTaskLoading = true;
        state.quickTaskError = null;
      })
      .addCase(
        fetchQuickTaskThunk.fulfilled,
        (state, action: PayloadAction<{ data: QuickTask[] }>) => {
          state.quickTaskLoading = false;
          state.quickTask = action.payload.data;
        }
      )
      .addCase(fetchQuickTaskThunk.rejected, (state, action) => {
        state.quickTaskLoading = false;
        state.quickTaskError = action.payload as string;
      });

    builder
      .addCase(createBulkTaskThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createBulkTaskThunk.fulfilled,
        (state, action: PayloadAction<{ success: boolean; tasks: Task[] }>) => {
          state.loading = false;
        }
      )
      .addCase(createBulkTaskThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setSearchFilter,
  setMyTasksOnly,
  setVesselStatusFilter,
  setTaskTypeFilter,
  clearFilters,
  moveTask,
  reorderTasks,
} = tasksSlice.actions;

export default tasksSlice.reducer;
